import { useState, type ReactNode } from "react";
import clsx from "clsx";
import styles from "./DataTable.module.scss";

/**
 * One column. `align: "end"` is for FIGURES — it right-aligns and sets the mono face, because a column of
 * numbers only scans if the digits line up; text columns keep the body face.
 */
export interface DataTableColumn<T> {
  /** Stable key — React's, and the one a caller can key styling off. */
  key: string;
  header: ReactNode;
  align?: "start" | "end";
  cell: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: readonly T[];
  /** Stable row key. Prefer a real id — an index re-keys every row when the list is paged or refetched. */
  rowKey: (row: T, index: number) => string;
  /**
   * Rows per page. OMIT for an unpaged table (the common case). When set, a pager renders beneath and the
   * card's height stays put however long the list gets — legacy's settlement card uses 6.
   */
  pageSize?: number;
  /**
   * Cap the scroll region, e.g. `"28rem"`. A long table otherwise pushes every section beneath it off the
   * page — a band with 52 tracked markets did exactly that. Omit to let the table run its natural height.
   */
  maxBlockSize?: string;
  /** Accessible name for the pager's nav landmark; only needed when `pageSize` is set. */
  label?: string;
  className?: string;
}

/**
 * A bounded, optionally paged data table.
 *
 * Extracted 2026-09-01 from the two booking surfaces, which had FOUR instances of the same markup and eight
 * byte-identical style rules copied between their sheets. It owns the three things every copy got right the
 * same way and would eventually have drifted on: the scroll region, the mono-caps header with numeric
 * alignment, and the pager.
 *
 * It owns NO cell content — every cell is the caller's `ReactNode`, so a pill, a link or a formatted figure
 * stays the feature's business. This is a layout primitive, not a grid: no sorting, no selection, no column
 * resizing. Add those when a surface actually needs them rather than in anticipation.
 *
 * **Horizontal scroll is inside the region, never the page** — a wide table must not make the body scroll
 * sideways. And there is deliberately no `overscroll-behavior: contain` on it: that reads like the right
 * thing for a scroll box, but it blocks scroll CHAINING at the boundary too, so a reader scrolling THROUGH
 * the card gets stuck once the last row is reached.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  pageSize,
  maxBlockSize,
  label,
  className,
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);

  const pageCount = pageSize ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  // Clamp rather than reset. A refetch that shortens the list must not strand the reader on a page that no
  // longer exists, and setting state during render would be the wrong way to fix that.
  const safePage = Math.min(page, pageCount - 1);
  const view = pageSize ? rows.slice(safePage * pageSize, safePage * pageSize + pageSize) : rows;

  return (
    <>
      <div className={clsx(styles.wrap, className)} style={maxBlockSize ? { maxBlockSize } : undefined}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={clsx(styles.th, c.align === "end" && styles.num)}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((row, i) => (
              <tr key={rowKey(row, i)}>
                {columns.map((c) => (
                  <td key={c.key} className={clsx(styles.td, c.align === "end" && styles.num)}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <nav className={styles.pager} aria-label={label ?? "Pages"}>
          <button
            type="button"
            className={styles.pagerNav}
            aria-label="Previous page"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
          >
            ‹
          </button>
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={styles.pagerPage}
              data-active={i === safePage || undefined}
              aria-current={i === safePage ? "page" : undefined}
              onClick={() => setPage(i)}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            className={styles.pagerNav}
            aria-label="Next page"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
          >
            ›
          </button>
        </nav>
      )}
    </>
  );
}
